// Pedidos: la tabla public.orders usa los nombres que escribe el bot (client_name, client_phone, delivery_address, payment_method, total) y,
// en pedidos más viejos, direccion_entrega y metodo_pago. La app leía customer_* y total_price, que no existen: aquí queda un solo lugar
// para leer un pedido (con respaldo a los nombres viejos) y para armar uno manual con las columnas reales.

export const PICKUP_LABEL = 'Presencial / Mostrador';

const text = (v) => (v == null ? '' : String(v).trim());

// Un pedido tal como se muestra: nombre, teléfono, dirección, total, medio de pago, origen y si es para recoger.
export const orderView = (order) => {
  const o = order || {};
  const totalRaw = o.total ?? o.total_price;
  const total = Number(totalRaw);
  const phone = text(o.client_phone ?? o.customer_phone).replace('@s.whatsapp.net', '');
  const address = text(o.delivery_address) || text(o.direccion_entrega) || text(o.customer_address);
  return {
    name: text(o.client_name) || text(o.customer_name),
    phone: phone === 'N/A' ? '' : phone,
    address,
    total: Number.isFinite(total) ? total : 0,
    payment: text(o.payment_method) || text(o.metodo_pago),
    source: text(o.source) || 'whatsapp',
    city: text(o.delivery_city),
    pickup: o.delivery_mode === 'recoger_local' || o.delivery_mode === 'pickup',
    paid: o.is_paid === true
  };
};

// Productos elegidos del catálogo para un pedido a mano: { id, nombre, precio, cantidad }. Sumar el mismo producto aumenta su cantidad.
export const addOrderLine = (lines, product, price) => {
  const id = String(product.id);
  const found = lines.find((l) => l.id === id);
  if (found) return lines.map((l) => (l.id === id ? { ...l, cantidad: l.cantidad + 1 } : l));
  return [...lines, { id, nombre: text(product.name), precio: Number(price) || 0, cantidad: 1 }];
};

// Cantidad 0 o menos quita la línea.
export const setOrderLineQty = (lines, id, qty) => {
  const n = Math.floor(Number(qty));
  if (!Number.isFinite(n) || n <= 0) return lines.filter((l) => l.id !== String(id));
  return lines.map((l) => (l.id === String(id) ? { ...l, cantidad: n } : l));
};

export const orderLinesTotal = (lines) => lines.reduce((sum, l) => sum + l.cantidad * l.precio, 0);

// Fila de un pedido hecho a mano, con las columnas reales de la tabla. Sin dirección es para recoger en el local.
// Si se eligieron productos del catálogo (`form.lines`) van como líneas reales (nombre, cantidad, precio) y el texto libre queda como nota;
// si no, el texto va como una sola línea. `source` (origen) es una columna que agrega la migración 20261006a_orders_source.sql: el guardado la prueba aparte (ver useOrders).
export const buildManualOrderRow = (form, businessId) => {
  const lines = Array.isArray(form.lines) ? form.lines : [];
  const note = text(form.items_text);
  const total = Number(form.total) || (lines.length ? orderLinesTotal(lines) : 0);
  const address = text(form.customer_address);
  const items = lines.length
    ? [...lines.map((l) => ({ nombre: l.nombre, cantidad: l.cantidad, precio: l.precio })), ...(note ? [{ nombre: note, cantidad: 1, precio: 0 }] : [])]
    : [{ nombre: note, cantidad: 1, precio: total }];
  return {
    business_id: String(businessId),
    client_name: text(form.customer_name),
    client_phone: text(form.customer_phone),
    delivery_address: address || PICKUP_LABEL,
    delivery_mode: address ? 'domicilio' : 'recoger_local',
    items,
    total,
    status: 'pending',
    // La ciudad solo se manda si se eligió: `delivery_city` la crea la migración 20261007d y sin ella un pedido sin ciudad se guarda igual.
    ...(text(form.delivery_city) ? { delivery_city: text(form.delivery_city) } : {}),
    ...(text(form.payment_method) ? { payment_method: text(form.payment_method) } : {})
  };
};

// Cómo se muestra la forma de pago de un pedido (el bot guarda textos como «nequi» o «efectivo»; el minisitio, «efectivo» o «transferencia»).
export const paymentLabel = (payment) => {
  const p = text(payment).toLowerCase();
  if (!p) return 'Pago sin definir';
  if (p === 'efectivo') return 'Efectivo al recibir';
  if (p === 'transferencia') return 'Transferencia';
  return p.charAt(0).toUpperCase() + p.slice(1);
};

// Pagos que no se cobran en mano: transferencia, Nequi, Daviplata, PSE, tarjeta… (el efectivo y el pago sin definir sí se cobran al entregar).
export const isDigitalPayment = (payment) => {
  const p = text(payment).toLowerCase();
  return Boolean(p) && !/efectivo|contra\s?entrega/.test(p);
};

// Número del pedido para mostrar y para relacionarlo con su pago: «#12» (consecutivo por negocio, migración 20261008f). Los pedidos sin número usan el código corto de siempre.
export const orderNumber = (o) => (o && o.order_number ? `#${o.order_number}` : `#${String((o && o.id) || '').substring(0, 6).toUpperCase()}`);

// Busca un pedido por su número («12», «#12», «pedido 12»). Solo cuenta si hay un número; devuelve el pedido o null.
export const findOrderByNumber = (orders, raw) => {
  const n = Number(String(raw ?? '').replace(/\D/g, ''));
  if (!Number.isInteger(n) || n <= 0) return null;
  return (orders || []).find((o) => Number(o.order_number) === n) || null;
};

// Pedido por transferencia que todavía no se paga (y no está cancelado): a quien se le puede recordar el pago.
export const isPendingTransfer = (o) => {
  if (!o) return false;
  if (!/transfer/i.test(String(o.payment_method ?? o.metodo_pago ?? ''))) return false;
  if (['cancelled', 'cancelled_by_client'].includes(o.status)) return false;
  return !(o.is_paid === true || o.estado_pago === 'Pagado');
};

// Texto del recordatorio de pago (mismo estilo que el que envía el flujo automático de n8n).
export const paymentReminderText = (order, business) => {
  const v = orderView(order);
  const bank = String((business && business.payment_info) || '').trim();
  const first = (v.name || '').split(' ')[0];
  return [
    `Hola${first ? ` ${first}` : ''} 👋 te escribimos de ${(business && business.nombre_negocio) || 'tu negocio'}.`,
    `Tu pedido ${orderNumber(order)} por $${Number(v.total || 0).toLocaleString('es-CO')} está pendiente de pago por transferencia.`,
    bank ? `\nDatos para pagar:\n${bank}` : '',
    `\nCuando hagas el pago, envíanos aquí el comprobante e indícanos el número del pedido (${orderNumber(order)}). ¡Gracias!`
  ].filter(Boolean).join('\n');
};

// Cuando el domiciliario entrega un pedido en efectivo sin cobrarlo, el pedido queda entregado pero por cobrar (migración 20261008d deja esta nota).
export const UNPAID_DELIVERY_NOTE = 'Entregado sin cobrar';

// ¿El pedido ya está pagado? Marca de pago, o entregado (salvo que el domiciliario avisó que no cobró), o pago confirmado por otros medios.
export const isPaidOrder = (o) => {
  if (!o) return false;
  if (o.is_paid === true || o.is_paid) return true;
  if (o.estado_pago === 'Pagado') return true;
  return o.status === 'delivered' && String(o.delivery_note || '').trim() !== UNPAID_DELIVERY_NOTE;
};

export const isMissingColumnError = (error, column) =>
  Boolean(error) && (['PGRST204', '42703'].includes(error.code) || /column/i.test(error.message || '')) && new RegExp(column, 'i').test(error.message || '');
